// #region imports

// --- Angular
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  Router,
  Event,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError
} from '@angular/router';

// --- RxJs
import { Observable, Subscription, of, fromEvent, combineLatest } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { User } from 'oidc-client';
import { User as OidcUser } from 'oidc-client';

// -- HS Code
import { fadeAnimation } from './app.animation';
import { AppService } from './core/services/app.service';
import { LanguagesBaseService } from './core/services/system/languages-base.service';
import { OidcService } from './core/services/account/oidc.service';
import { ProfileBaseService } from './core/services/profile/profile-base.service';
import { ApplicationError, LanguageModel, UserProfileModel, UserDemographicSelectList, Constants } from './models';
import { NgxSpinnerService } from 'ngx-spinner';
import { UserNotificationService } from './core/services/notifications/user-notification.service';
import { CommunicationsBaseService } from './core/services/profile/communications-base.service';
import { UserDemographicsService } from './core/services/profile/user-demographics.service';
import { UserDemographicsBaseService } from './core/services';
import { CookiePolicyService } from './core';
import { map, tap } from 'rxjs/operators';

// #endregion

@Component({
  selector: 'hs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fadeAnimation]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Heartstyles Indicator - Personal Edition';
  isLoading$: Observable<boolean>;
  identity$: Observable<User>;
  error$: Observable<ApplicationError>;
  languages$: Observable<LanguageModel[]>;
  selectedLanguage$: Observable<LanguageModel>;

  cookiePolicyLink = Constants.PRIVACY_POLICY_LINK;
  cookiePolicyAccepted$: Observable<boolean>;

  isRouting = false;

  isLoading: boolean;
  isLoadingSubscription: Subscription;

  identity: OidcUser;
  identitySubscription: Subscription;

  // profile: UserProfileModel;
  // profileSubscription: Subscription;
  settingsSubscription: Subscription;

  onlineEvent$: Observable<Event>;
  offlineEvent$: Observable<Event>;
  onlineSubscription: Subscription;
  offlineSubscription: Subscription;
  connectionStatusMessage: string;
  connectionStatus: string;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private appService: AppService,
    private oidcService: OidcService,
    private profileService: ProfileBaseService,
    private languageService: LanguagesBaseService,
    private spinner: NgxSpinnerService,
    private notificationService: UserNotificationService,
    private communicationService: CommunicationsBaseService,
    private userDemographicValueService: UserDemographicsBaseService,
    private cookiePolicyService: CookiePolicyService,
  ) {
    this.translate.setDefaultLang('en-GB');
    this.translate.use('en-GB');
    this.isLoading = false;
  }

  ngOnInit(): void {
    this.settingsSubscription = this.profileService.settings$.subscribe(settings => {
      if (!settings) {
        return;
      }

      this.languageService.initialiseLanguage(settings.languageId);
    });

    // subscribe to isLoading - this is so that we can disable the help link in the toast message if the application is loading
    this.isLoadingSubscription = this.appService.isLoading$.subscribe(isLoading => {
      this.isLoading = isLoading;
      if (isLoading) {
        this.spinner.show();
        return;
      }
      this.spinner.hide();
    });

    // subscribe to the identity - this is to trigger profile load when the logged-in user changes
    this.identitySubscription = this.oidcService.identity$.subscribe((oidcUser: OidcUser) => {
      this.identity = oidcUser;
      if (oidcUser) {
        this.profileService.loadProfile();
        this.communicationService.loadUserCommunicationPreferences();
        this.userDemographicValueService.loadUserDemographicValues();
      }
    });

    this.error$ = this.appService.lastError$;
    this.isLoading$ = this.appService.isLoading$;
    this.identity$ = this.oidcService.identity$;
    this.languages$ = this.languageService.languages$;
    this.selectedLanguage$ = this.languageService.selectedLanguage$;

    this.cookiePolicyAccepted$ = combineLatest(
      this.cookiePolicyService.accepted$,
      this.cookiePolicyService.initialized$
    ).pipe(
      map(([accepted, initialized]) => accepted && initialized),
    );

    this.languageService.loadLanguages();
    this.oidcService.getOidcUser();
    this.cookiePolicyService.init();

    this.router.events.subscribe((routerEvent: Event) => {
      this.checkRouterEvent(routerEvent);
    });

    this.onlineEvent$ = fromEvent<Event>(window, 'online');
    this.offlineEvent$ = fromEvent<Event>(window, 'offline');

    this.onlineSubscription = this.onlineEvent$.subscribe(e => {
      this.connectionStatusMessage = this.translate.instant('connectionStatusMessageOnline');
      this.connectionStatus = 'online';
    });

    this.offlineSubscription = this.offlineEvent$.subscribe(e => {
      this.connectionStatusMessage = this.translate.instant('connectionStatusMessageOffline');
      this.connectionStatus = 'offline';
    });
  }

  ngOnDestroy(): void {
    this.isLoadingSubscription.unsubscribe();
    this.isLoadingSubscription = null;
    this.identitySubscription.unsubscribe();
    this.identitySubscription = null;

    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
      this.settingsSubscription = null;
    }

    // this.profileSubscription.unsubscribe();
    // this.profileSubscription = null;

    if (this.onlineSubscription) {
      this.onlineSubscription.unsubscribe();
      this.onlineSubscription = null;
    }

    if (this.offlineSubscription) {
      this.offlineSubscription.unsubscribe();
      this.offlineSubscription = null;
    }
  }

  checkRouterEvent(routerEvent: Event): void {
    if (routerEvent instanceof NavigationStart) {
      this.isRouting = true;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.isRouting = false;
      // scroll to the top: on route change
      window.scrollTo(0, 0);
    }
  }

  clearAlert(): void {
    this.appService.setLastError(null);
  }

  onLogin(): void {
    this.appService.setLoading(true);
    this.oidcService.signInRedirect();
  }

  onLogout(): void {
    this.appService.setLoading(true);
    this.oidcService.signOutRedirect();
  }

  checkActive(e): void {
    if (this.isLoading) {
      e.preventDefault();
    }
  }

  onLanguageChanged(newLanguage: LanguageModel): void {
    const translateLanguageAndCulture = newLanguage.cultureCode ?
      `${newLanguage.isoTwoLetterName}-${newLanguage.cultureCode}` :
      `${newLanguage.isoTwoLetterName}`;
    this.languageService.setSelectedLanguage(newLanguage);
    this.translate.use(translateLanguageAndCulture);
  }

  onNetworkCallToAction(): void {
    window.location.reload();
  }

  onNetworkAlertClose(): void {
    console.log('close');
  }

  onCookiePolicyAccept() {
    this.cookiePolicyService.accept();
  }

  }
